import React, { useEffect } from 'react';

import { createStyles, Theme, WithStyles, withStyles, withTheme, WithTheme } from '@material-ui/core';
import { LiveHelpOutlined } from '@material-ui/icons';
import classNames from 'classnames';
import { toString } from 'lodash';

import configService from '@lib/services/configService';

import { Fab } from 'components/v2';
import { LogoFile } from 'containers/App/contexts/BrandingContext';
import authResource from 'resources/authResource';

const pathsWithNoWidget = /^\/join|^\/login|^\/zendesk\/login|^\/telehealth/i;

interface ZendeskWidgetProps {
  isCobrandingEnabled: boolean;
  nextyStorageValue: boolean;
  cobrandingLogo?: LogoFile;
  theme: Theme;
}
const POSITION_NEXTY = '8px';

function getZendeskLauncher() {
  const button = document.querySelector<HTMLDivElement>('#myLauncher');
  return button;
}

function openWidget() {
  (window as any).zE?.('messenger', 'show');
  (window as any).zE?.('messenger', 'open');
  const button = getZendeskLauncher();
  if (button) {
    button.style.opacity = '0';
  }
}

const styles = (theme: Theme) =>
  createStyles({
    button: {
      position: 'absolute',
      // using the drawer zIndex + 1 as reference to avoid conflicts between zendesk widget and other components using the older version MUI theme
      zIndex: theme.zIndex.drawer + 1,
      opacity: '0',
    },

    buttonOldUx: {
      left: '24px',
      bottom: '56px',
    },
    buttonOldUxHasLogo: {
      left: '180px',
      bottom: '56px',
    },
    buttonNewUx: {
      left: POSITION_NEXTY,
      bottom: POSITION_NEXTY,
    },
  });
interface CustomButtonProps extends WithTheme, WithStyles<typeof styles> {
  hasPoweredLumaLogo: boolean;
  nextyStorageValue: boolean;
}

const CustomButton = ({ classes, nextyStorageValue, hasPoweredLumaLogo }: CustomButtonProps) => (
  <Fab
    color="supportPowder"
    className={classNames(classes.button, {
      [classes.buttonOldUx]: !nextyStorageValue && !hasPoweredLumaLogo,
      [classes.buttonOldUxHasLogo]: !nextyStorageValue && hasPoweredLumaLogo,
      [classes.buttonNewUx]: nextyStorageValue,
    })}
    onClick={openWidget}
    id="myLauncher"
  >
    <LiveHelpOutlined />
  </Fab>
);

const StyledCustomButton = withStyles(styles)(CustomButton);

const ZendeskWidget = ({ isCobrandingEnabled, nextyStorageValue, cobrandingLogo, theme }: ZendeskWidgetProps) => {
  const currentLocation = window.location.pathname;
  const hasPoweredLumaLogo = isCobrandingEnabled && !!cobrandingLogo;

  // Insert script
  useEffect(() => {
    const zendeskKey = configService.getKeyZendesk() || '';
    const script = document.createElement('script');
    script.async = true;
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
    document.body.appendChild(script);

    script.onload = () => {
      (window as any).zE?.('messenger:on', 'open', () => {
        (window as any).zE?.('messenger', 'loginUser', (callback: (token: string) => void) => {
          authResource.getZendeskToken().then((zDToken) => {
            const sanitizedToken = zDToken.replace(/['"]+/g, '');
            callback(sanitizedToken);
          });
        });
      });

      (window as any).zE?.('messenger:on', 'close', () => {
        (window as any).zE?.('messenger', 'hide');
        const button = getZendeskLauncher();
        if (button) {
          button.style.opacity = '1';
        }
      });
    };
  }, []);

  // Insert style
  useEffect(() => {
    const verticalPositionOldUX = hasPoweredLumaLogo ? '180px' : '70px';
    // Add custom CSS styles for Zendesk messenger iframe with dynamic positioning
    const style = document.createElement('style');
    style.textContent = `
        iframe[title="Messaging window"] {
          bottom: ${nextyStorageValue ? POSITION_NEXTY : verticalPositionOldUX} !important; /* Vertical adjustment */
          right: ${nextyStorageValue ? POSITION_NEXTY : '-10px'} !important;  /* Horizontal adjustment */
        }
      `;
    document.head.appendChild(style);
  }, [nextyStorageValue, hasPoweredLumaLogo]);

  // hide widget and show
  useEffect(() => {
    if (pathsWithNoWidget.test(window.location.pathname)) {
      // TODO(sergio): add zE to the Windows interface
      (window as any).zE?.('messenger', 'hide');
      (window as any).zE?.('messenger', 'logoutUser');
      (window as any).zE?.('messenger', 'close');
      const button = getZendeskLauncher();
      if (button) {
        button.style.opacity = '0';
        button.style.zIndex = '-9999';
      }
    } else {
      const button = getZendeskLauncher();
      if (button) {
        button.style.opacity = '1';
        button.style.zIndex = toString(theme.zIndex.drawer + 1);
      }
    }
  }, [currentLocation, theme.zIndex.drawer]);

  return (
    <StyledCustomButton hasPoweredLumaLogo={hasPoweredLumaLogo} nextyStorageValue={nextyStorageValue} theme={theme} />
  );
};

export default withTheme()(withStyles(styles)(ZendeskWidget));
